import axios from "axios";
import {
  CartInput,
  OfferUIDetaislParams,
} from "../../services/redux/types/CartLoyaltyV2Types";
import { OrderCartV2Input } from "../../services/redux/types/OrderCartV2Types";
import {
  CUSTOMER_ANALYTICS_EVENT,
  FeedBackTagsEnum,
  LocalStorageEnum,
  PagesEnum,
  ReceiptItemStateEnum,
} from "../enums";
import env from "../env.config";
import { AlertErrorType } from "../ErrorTypes";
import { setAllCategoriesItemsCustomPricesPerFranchise } from "../help/pricing/pricing";
import LS from "../LocalStorage";
import {
  CustomerLoyaltyOffersParams,
  LoyaltyCustomerInfo,
  LoyaltyCustomerInfoParams,
  LoyaltyOfferType,
  LoyaltyTierSystem,
  LoyaltyTierSystemParams,
} from "../loyaltyTypes";
import {
  PromoBannersParams,
  PromoPopupDisplayParams,
  PromoPopupsParams,
} from "../promoMaterial/promoMaterialParams";
import {
  GetPromoBannersResponse,
  GetPromoPopupsResponse,
} from "../promoMaterial/promoMaterialTypes";
import {
  debugLog,
  sanitizeAlertData,
  sanitizeAlertIdentity,
} from "../telemetrySanitization";
import {
  AnalyticsExtraInfo,
  CategoryType,
  DeliveryReceipt,
  PAGER_DUTY_ERROR_URGENCY,
  ReceiptExtras,
  StoreRedirectParams,
  confirmReceiptBodyType,
  toggleNotificationReqBody,
  userOffersRes,
} from "../types";
import { CreateReceiptBodyTypeFlat } from "../types/createReceipt";
import { fetchData } from "./buildCalls";

const allowedAlertTexts = new Set<string>(Object.values(AlertErrorType));

export const sendPartnerEmail = async (
  organizationName: string,
  mail?: string
) => {
  return await axios.post(`/auth/send-verify-mail`, {
    organizationName,
    mail,
  });
};

export const resendVerifyPartnerEmail = async (
  mail: string | string[] | undefined
) => {
  return await axios.post(`/auth/resend-verify-mail`, {
    mail,
  });
};

/**
 * Reports this session's Meta identifiers so the backend can replay them onto
 * every event it sends.
 *
 * Carries only the two cookies, which exist solely in the browser. The client IP
 * and user agent are read by the backend from the request headers, so they never
 * appear in the page's network traffic -- deliberate, and the reason this is a
 * separate call rather than extra fields on `customerAnalytics`.
 */
export const reportMetaSession = (payload: {
  restaurantId?: number;
  franchiseSlug?: string;
  fbc?: string;
  fbp?: string;
}) => {
  return axios.post(`/user/marketing-session`, payload);
};

export const customerAnalytics = (
  event: CUSTOMER_ANALYTICS_EVENT,
  extraInfo?: AnalyticsExtraInfo,
  franchiseId?: number,
  restaurantId?: number,
  franchiseSlug?: string
) => {
  return axios.post(`/user/customer-analytics`, {
    franchiseId,
    restaurantId,
    franchiseSlug,
    event,
    extraInfo,
  });
};

export const getReceiptByPaymobOrderId = async (orderId: string) => {
  return await axios.get(`/receipt/paymob/${orderId}`);
};

export const getPaymentMethods = async () => {
  return await axios.get(`/user/paymob/cards`);
};

export const getAllOrganizations = async (restaurantId: number) => {
  return await axios.get(`/organizations/all-organizations/${restaurantId}`);
};

export const getPreviousOrders = async (restaurantId: number) => {
  return await axios.get(`/receipt/previous-orders`, {
    params: {
      restaurantId,
    },
  });
};

export const getLatestReceipt = async (franchiseId: number) => {
  return await axios.get(`receipt/latest-receipt/${franchiseId}`);
};

export const getReceiptById = async (receiptId: number) => {
  return await axios.get(`/receipt`, {
    params: {
      receiptId,
    },
  });
};

export const getActiveReceipt = async (
  franchiseId: number
): Promise<{ data: number }> => {
  return await axios.get(`receipt/active-receipt`, { params: { franchiseId } });
};

export const getActiveReceiptPerRestaurant = async (
  restaurantId: number
): Promise<{ data: number }> => {
  return await axios.get(`receipt/active-receipt-per-restaurant`, {
    params: { restaurantId },
  });
};

export const getUpdatedReceipt = async (
  receiptId: number,
  restaurantId: number
): Promise<{
  receiptState: ReceiptItemStateEnum;
  currentPoints?: number;
  transactionPoints?: number;
  hasFeedback: boolean;
  paidAt: Date | null;
  confirmedAt: Date | null;
  prepTime: number;
  receiptId: number;
  displayedReceiptId: number;
  receiptExtras: ReceiptExtras;
  deliveryReceipt: DeliveryReceipt;
}> => {
  const res = await axios.get(`receipt/updated-receipt-state`, {
    params: { restaurantId, receiptId },
  });

  return { ...res.data, receiptId };
};

export const refetchItemsInfoWithRestaurantId = async (
  restaurantId: number,
  franchiseSlug: string
): Promise<{
  outOfStockItemsIds?: number[];
  disabledItemsIds?: number[];
  disabledItemsPerOrderType?: {
    TAKE_OUT: number[];
    DINE_IN: number[];
    DELIVERY: number[];
    DELIVERYV2: number[];
  };
  itemsWithCustomDeliveryTimes?: {
    [itemId: string]: number;
  };
  offersItems?: any;
}> => {
  return (
    await axios.get(`items/status`, {
      params: { restaurantId, franchiseSlug },
    })
  ).data;
};

export const fetchCategoriesForBranch = async (
  restaurantId: number,
  franchiseSlug?: string
): Promise<CategoryType[]> => {
  const url = franchiseSlug
    ? `/category/details/${restaurantId}?franchiseSlug=${franchiseSlug}`
    : `/category/details/${restaurantId}`;
  const res = await fetchData(url, env.apiV2);
  if (res.status > 399) {
    throw new Error(`Failed to fetch branch categories: ${res.status}`);
  }

  return res.json();
};

export const getVisitorToken = async () => {
  const isPartnerAppUser = LS._get(LocalStorageEnum.MOBILE_APP_PARTNER);
  const isMobileAppUser = LS._get(LocalStorageEnum.MOBILE_APP_RESTAURANT);
  const isWebSelfServiceUser = LS._get(LocalStorageEnum.SELF_SERVICE, true);

  return await axios.post(`user/create-visitor`, {
    isMobileAppUser,
    isPartnerAppUser,
    isWebSelfServiceUser,
  });
};

export const createLinkTrackingVisit = (referenceName: string) => {
  return axios.post("link-tracking/visit", {
    referenceName,
  });
};

export const createLinkTrackingReceipt = (
  referenceName: string,
  receiptId: number
) => {
  return axios.post("link-tracking/receipt", {
    referenceName,
    receiptId,
  });
};

export const getItemDetails = async (itemId: number) => {
  return await axios.get(`items/item/${itemId}`);
};

export const getReferralCode = async (restaurantId: number) => {
  return await axios.post(`referralcode/info`, { restaurantId });
};

export const getNotifications = async (restaurantId: number) => {
  return await axios.get(`notification/${restaurantId}`);
};

export const toggleNotifications = async (body: toggleNotificationReqBody) => {
  return await axios.post(`notification/toggle-notification`, body);
};

export const deleteCardDetail = async (card: number) => {
  return await axios.post("/user/paymob/delete-card", card).then(() => {});
};

export const deleteAddress = async (addressId: number) => {
  return await axios
    .post("/delivery/delete-address", { addressId })
    .then(() => {})
    .catch((error) => {
      console.error(error);
    });
};

export const sendFeedback = async (
  numberOfStars: number,
  feedback: string,
  receiptId: number,
  feedbackTags?: FeedBackTagsEnum[],
  feedbackTagsText?: String
) => {
  return await axios.post("/feedback", {
    numberOfStars,
    feedback,
    receiptId,
    feedbackTags,
    feedbackTagsText,
  });
};

export const confirmReceipt = async (reqBody: confirmReceiptBodyType) => {
  return await axios.post(`/receipt/confirm`, reqBody);
};

export const createReceipt = async (reqBody: CreateReceiptBodyTypeFlat) => {
  return await axios.post(`/receipt/create`, reqBody);
};

/**
 * The customer is back from the payment gateway without having paid. Cancels the
 * unpaid order so the loyalty service releases the points and offer it is
 * holding right away, instead of keeping them locked for the rest of the hold
 * window. Refused server-side if the order turns out to be paid.
 */
export const abandonUnpaidReceipt = async (receiptId: number) => {
  return await axios.post(`/receipt/${receiptId}/abandon-unpaid`);
};

export const getPosReceiptState = async (
  franchiseId: number,
  receiptId: number
) => {
  return await axios.get(`/receipt/pos/${franchiseId}/${receiptId}`);
};

export const cancelPosReceipt = async (
  franchiseId: number,
  deviceNumber: number
) => {
  return await axios.post(`/receipt/cancel-pos-receipt`, {
    franchiseId,
    deviceNumber,
  });
};

export const generateOtp = async (
  phoneNumber: string,
  franchiseId: number,
  countryCode?: string,
  name?: string,
  code?: string,
  login?: boolean,
  turnstileToken?: string
) => {
  const isPartnerAppUser = LS._get(LocalStorageEnum.MOBILE_APP_PARTNER);
  const isMobileAppUser = LS._get(LocalStorageEnum.MOBILE_APP_RESTAURANT);
  // Parse inside LS._get (guarded) so a blocked/empty store can never throw
  // here and abort the request before it is sent.
  const isWebSelfServiceUser = LS._get(LocalStorageEnum.SELF_SERVICE, true);

  return await axios.post(`/auth/otp`, {
    phoneNumber,
    countryCode,
    name,
    code,
    franchiseId,
    login,
    isPartnerAppUser,
    isMobileAppUser,
    isWebSelfServiceUser,
    turnstileToken,
  });
};

export type VerifyOtpUserExtra = {
  email?: string;
  organization?: string;
  position?: string;
  dateOfBirth?: string;
  gender?: string;
  posName?: string;
  numberOfBranches?: string;
};

export const verifyOtpCode = async (
  phoneNumber: string,
  code: string,
  name?: string,
  userExtraInfo?: VerifyOtpUserExtra,
  restaurantId?: number
) => {
  const isPartnerAppUser = LS._get(LocalStorageEnum.MOBILE_APP_PARTNER);
  const isMobileAppUser = LS._get(LocalStorageEnum.MOBILE_APP_RESTAURANT);
  const isWebSelfServiceUser = JSON.parse(
    LS._get(LocalStorageEnum.SELF_SERVICE) ?? "false"
  );
  const e = userExtraInfo;
  const payload: Record<string, unknown> = {
    phoneNumber,
    name,
    code,
    restaurantId,
    isPartnerAppUser,
    isMobileAppUser,
    isWebSelfServiceUser,
  };
  (
    [
      "email",
      "organization",
      "position",
      "dateOfBirth",
      "gender",
      "posName",
      "numberOfBranches",
    ] as const
  ).forEach((key) => {
    if (e?.[key] !== undefined) {
      payload[key] = e[key];
    }
  });

  return await axios.post(`/auth/check-otp`, payload);
};

export const findLoyaltyPass = async (
  customerId: number,
  restaurantId: number
) => {
  return await axios.get(`/apple-wallet/pass/${customerId}/${restaurantId}`);
};

export const getAppleRegisteredPass = async (
  customerId: number,
  restaurantId: number,
  customerShortId?: string
): Promise<{ data: { exists: boolean } }> => {
  return await axios.get(
    `/apple-wallet/registered-pass/${customerId}/${restaurantId}`,
    customerShortId ? { params: { customerShortId } } : undefined
  );
};

export const getGoogleRegisteredPass = async (
  customerId: number,
  restaurantId: number,
  customerShortId?: string
): Promise<{ data: { exists: boolean } }> => {
  return await axios.get(
    `/google-wallet/registered-pass/${customerId}/${restaurantId}`,
    customerShortId ? { params: { customerShortId } } : undefined
  );
};

export const generateAppleWalletPass = async (
  customerId: number,
  restaurantId: number
) => {
  return await axios.post(
    `/apple-wallet/pass/generate`,
    {
      customerId,
      restaurantId,
    },
    {
      responseType: "blob",
    }
  );
};

export const generateGoogleWalletPass = async (
  customerId: number,
  restaurantId: number
) => {
  return await axios.post(`/google-wallet/create-pass-object`, {
    customerId,
    restaurantId,
  });
};

export const registerAppleWalletDevice = async (
  deviceLibraryIdentifier: string,
  passTypeIdentifier: string,
  serialNumber: string,
  pushToken: string,
  authenticationToken: string
) => {
  return await axios.post(
    `/apple-wallet/v1/devices/${deviceLibraryIdentifier}/registrations/${passTypeIdentifier}/${serialNumber}`,
    {
      pushToken,
    },
    {
      headers: {
        Authorization: `ApplePass ${authenticationToken}`,
      },
    }
  );
};

export const getCustomerAddresses = async () => {
  return await axios.get(`/delivery/address`);
};

export const addCustomerAddress = async (
  area: string | null,
  streetName: string | null,
  buildingNumber: string | null,
  floorNumber: string | null,
  apartmentNumber: string | null,
  addressTitle: string,
  additionalDetails: string | null,
  lng: string,
  lat: string,
  deliveryTypeId?: number,
  addressId?: number
) => {
  return await axios.post(`/delivery/address`, {
    area,
    streetName,
    buildingNumber,
    floorNumber,
    apartmentNumber,
    addressTitle,
    additionalDetails,
    lat,
    lng,
    id: addressId,
    deliveryTypeId,
  });
};

export const alert = async (
  text: string,
  data: any,
  severity: PAGER_DUTY_ERROR_URGENCY = PAGER_DUTY_ERROR_URGENCY.LOW,
  printOnly?: boolean
) => {
  if (data?.key === "table-page:car - pickup") {
    return;
  }

  const sanitizedData = sanitizeAlertData(data);
  const sanitizedText = allowedAlertTexts.has(text)
    ? text
    : sanitizeAlertIdentity(text);
  debugLog(sanitizedText, sanitizedData);

  return await axios.post(`/user/alert`, {
    text: sanitizedText,
    data: sanitizedData,
    severity,
    printOnly,
  });
};

/** Keep useful client diagnostics in backend logs without paging on-call. */
export const logClientError = (text: string, data: any) =>
  alert(text, data, PAGER_DUTY_ERROR_URGENCY.LOW, true);

export const updateMenuIfMenuVersionChanged = (
  menuVersion: number,
  menuLS: any,
  restaurantId: number,
  buildAllCategories: CategoryType[],
  pageEnum: PagesEnum,
  // Only ever called with concrete category arrays — accepts plain appliers
  // (e.g. home's setCategoriesWithLookup, which also refreshes menu lookups).
  setAllNewCategories?: (value: CategoryType[]) => void,
  franchiseId?: number
) => {
  fetchData(
    `/category/details/${restaurantId}?menuVersion=${menuVersion}`,
    env.apiV2
  )
    .then((res) => {
      if (res.status > 399) {
        res.body?.cancel();

        return;
      }
      if (res.status === 304) {
        res.body?.cancel();
        if (!buildAllCategories && !menuLS?.categoriesData) {
          return;
        }
        if (pageEnum === PagesEnum.Home && setAllNewCategories && franchiseId) {
          if (menuLS?.menuVersion && menuLS?.categoriesData) {
            const categoriesFromMenuLS =
              setAllCategoriesItemsCustomPricesPerFranchise(
                menuLS?.categoriesData,
                franchiseId
              );
            setAllNewCategories(categoriesFromMenuLS);
          } else {
            const categoriesFromBuild =
              setAllCategoriesItemsCustomPricesPerFranchise(
                buildAllCategories,
                franchiseId
              );
            setAllNewCategories(categoriesFromBuild);
          }
        }

        return;
      }

      res
        .json()
        .then((data) => {
          LS._set(
            LocalStorageEnum.MENU_DATA,
            {
              menuVersion: data[0].restaurant.menuVersion,
              categoriesData: data,
            },
            true
          );
          if (
            pageEnum === PagesEnum.Home &&
            setAllNewCategories &&
            franchiseId
          ) {
            const categoriesFromApiCall =
              setAllCategoriesItemsCustomPricesPerFranchise(data, franchiseId);
            setAllNewCategories(categoriesFromApiCall);
          }
        })
        .catch((e) => {
          console.log("error=>", e);
        });
    })
    .catch((e) => {
      console.log("error=>", e);
    });
};

export const getOrganizationCustomerWithOffers = async (
  restaurantId: number
) => {
  return await axios.get(`organizations/organization-customer/${restaurantId}`);
};

export const disconnectOrganizationEmail = async () => {
  return await axios.get(`user/diconnect-organization-email`);
};

export const getOrganizationCustomerByCustomerIdAndRestaurantId = async (
  restaurantId: number
) => {
  return await axios.get(`user/organization`, {
    params: {
      restaurantId,
    },
  });
};

export const isOrganizationCustomerEmailVerified = async () => {
  return await axios.get(`user/email-verified`);
};

export const deleteAccount = async () => {
  return await axios.post(`user/delete`);
};

export type CustomerProfile = {
  name?: string;
  phoneNumber?: string;
  email?: string;
  dateOfBirth?: string | null;
  gender?: string | null;
};

export const getCustomerProfile = async (): Promise<{
  data: CustomerProfile;
}> => {
  return await axios.get(`user/profile`);
};

// ----------------------Core awards the points, and only when the stored value was NULL-----------------------
export const updateCustomerProfile = async (
  payload: Pick<CustomerProfile, "dateOfBirth" | "gender"> & {
    restaurantId?: number;
  }
): Promise<{ data: CustomerProfile & { pointsAwarded?: number } }> => {
  return await axios.post(`user/profile`, payload);
};

export const getPointingSystem = async (
  restaurantId?: number,
  splitedOfferFromList?: boolean,
  customerShortId?: string
) => {
  return await axios.get(`/pointing`, {
    params: {
      restaurantId,
      splitedOfferFromList,
      customerShortId,
    },
  });
};

export const getFranchiseIsOpen = async (franchiseId: number) => {
  return await axios.get(`franchise/is-open`, {
    params: {
      franchiseId,
    },
  });
};

export const getUserOffers = async (
  restaurantId: number,
  franchiseSlug?: string
): Promise<userOffersRes> => {
  return await axios.get(`user/link-offers`, {
    params: {
      restaurantId,
      franchiseSlug,
    },
  });
};

export const checkPromoCode = async (
  promoCodeName: string,
  restaurantId: number
) => {
  return await axios.post(`/promoCode/`, {
    promoCodeName,
    restaurantId,
  });
};

export const applyPromoCode = async (
  promoCode: string,
  franchiseId: number,
  restaurantId: number
) => {
  const res = await axios.post(
    `/proxy-loyalty/promo-code/apply`,
    { promoCode },
    {
      params: {
        franchiseId,
        restaurantId,
        offerFormat: "raw",
        offerUi: "full",
      },
    }
  );

  const data = res.data;
  const withPackageOffer =
    data &&
    typeof data === "object" &&
    data.valid === true &&
    "offer" in data &&
    data.offer !== null &&
    typeof data.offer === "object" &&
    !("packageOffer" in data)
      ? { ...(data as Record<string, unknown>), packageOffer: data.offer }
      : data;

  return {
    ...res,
    data: withPackageOffer,
  };
};

export const getCurrentScheduledSlots = async (franshisId: number) => {
  return await axios.get(`/schedule-slot/current`, {
    params: {
      franchiseId: franshisId,
    },
  });
};

export const getFranchiseDeliveryZoneSlots = async (
  franchiseId: number,
  deliveryZoneId: number
) => {
  return await axios.get(`/schedule-slot/franchise-delivery-zone-slots`, {
    params: {
      franchiseId,
      deliveryZoneId,
    },
  });
};

export const getLoyaltyEnrollmentByCode = async (code: string) => {
  return await axios.post(`user/loyalty-enrollment`, { code });
};

export const getLoyaltyWalletConfig = async (restaurantId: number) => {
  return await axios.get(`/loyalty-wallet-config/${restaurantId}`);
};

export const calculateCartWithOffers = async (params: CartInput) => {
  return await axios.post(`cart-loyalty/cart/calculate`, params, {
    timeout: 3000 * 10, // 30 seconds timeout
  });
};

export const calculateOrderCartV2 = async (
  params: OrderCartV2Input,
  restaurantId: number,
  signal?: AbortSignal,
  franchiseId?: number,
  /**
   * The delivery context the charge is priced from. Sending it lets the
   * backend resolve the fee from the address's zone instead of pricing
   * whatever `config.fees.deliveryFees` this cart carries — which is 0 until a
   * zone matches, and then quotes free delivery on an order that is charged
   * the real fee.
   */
  franchiseDeliveryConfigId?: number,
  customerAddressId?: number
) => {
  return await axios.post(`proxy-loyalty/order/calculate`, params, {
    params: {
      restaurantId,
      franchiseId,
      franchiseDeliveryConfigId,
      customerAddressId,
    },
    timeout: 3000 * 10,
    signal,
  });
};

export const getOffersWithUIDetails = async (params: OfferUIDetaislParams) => {
  return await axios.get(`cart-loyalty/offer/`, { params });
};

export const getCustomerLoyaltyOffers = async (
  params: CustomerLoyaltyOffersParams
): Promise<{ data: LoyaltyOfferType[] }> => {
  return await axios.get(`proxy-loyalty/customer/offers`, {
    params: {
      ...params,
      offerFormat: "raw",
      offerUi: "full",
      languages: env.supportedLanguages.join(","),
    },
  });
};

/**
 * Promo material (story #4087). The server owns which pop-up belongs to this
 * moment and which banners the rail shows; the client reports where it is and
 * what it has already been shown.
 *
 * `channel` comes from {@link getLoyaltyChannel} rather than a literal: this same
 * app runs inside the mobile webview and on self-service kiosks, and telling the
 * server "web" there would offer an app download inside the app.
 */
export const getPromoPopups = async (
  params: PromoPopupsParams
): Promise<{ data: GetPromoPopupsResponse }> => {
  return await axios.get(`/proxy-loyalty/promo-material/popups`, {
    params: {
      ...params,
      languages: env.supportedLanguages.join(","),
    },
  });
};

export const getPromoBanners = async (
  params: PromoBannersParams
): Promise<{ data: GetPromoBannersResponse }> => {
  return await axios.get(`/proxy-loyalty/promo-material/banners`, {
    params: {
      ...params,
      languages: env.supportedLanguages.join(","),
    },
  });
};

/**
 * Fire-and-forget: a lost record means the customer may see a pop-up once more,
 * which beats failing the screen they are on.
 */
export const postPromoPopupDisplay = async ({
  franchiseId,
  ...body
}: PromoPopupDisplayParams) => {
  return await axios.post(
    `/proxy-loyalty/promo-material/popup-displays`,
    body,
    { params: { franchiseId } }
  );
};

export const trackDeliveryOrder = async (
  deliveryReceiptId: number,
  referenceNumber: string
) => {
  return await axios.get(`roboost/trackOrder`, {
    params: {
      deliveryReceiptId,
      referenceNumber,
    },
  });
};

export const checkCustomerOfflineOffers = async (customerShortId: string) => {
  return await axios.get(
    `user/check-Customer-offline-offers?customerShortId=${customerShortId}`
  );
};

export const redirectToStore = async (
  params: StoreRedirectParams,
  config?: { signal?: AbortSignal }
) => {
  return await axios.get(`/mobile-app/store-redirect`, {
    params: {
      os: params.os,
      restaurantId: params.restaurantId,
      linkSource: params.linkSource,
      deviceId: params.deviceId,
    },
    signal: config?.signal,
  });
};

export const getDownloadAppPopupDetails = async (restaurantId: number) => {
  return await axios.get(`/mobile-app/download-app-popup`, {
    params: {
      restaurantId,
    },
  });
};

export const recordLoyaltyCardView = async (params: {
  qrcode: string;
  franchise?: number;
  host?: string;
  waiterName?: string;
}) => {
  return await axios.get(`/loyalty-cards/loyalty-card-view`, {
    params: {
      qrcode: params.qrcode,
      ...(params.franchise && { franchise: params.franchise }),
      ...(params.host && { host: params.host }),
      ...(params.waiterName && { waiterName: params.waiterName }),
    },
  });
};

export const getLoyaltyCustomerInfo = async (
  params: LoyaltyCustomerInfoParams
): Promise<{ data: LoyaltyCustomerInfo }> => {
  // Replaces the deprecated /proxy-loyalty/info/:customerId, whose path id the
  // proxy no longer reads - it derives the customer from the token or, for
  // offline-mode guests, from customerShortId.
  return await axios.get(`/proxy-loyalty/customer/info`, { params });
};

export const getLoyaltyTierSystemForCustomer = async (
  params: LoyaltyTierSystemParams
): Promise<{ data: LoyaltyTierSystem }> => {
  return await axios.get(`/proxy-loyalty/tier-system/customer`, {
    params: {
      offerFormat: "raw",
      offerUi: "full",
      restaurantId: params.restaurantId,
      languages: env.supportedLanguages.join(","),
      ...(params.customerShortId && {
        customerShortId: params.customerShortId,
      }),
    },
  });
};
